import { FC } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useSetRecoilState } from "recoil";

import { SVG_MUI } from "@ryde/assets";
import {
  BulkActionsPanel,
  IBulkActionItem,
  useGenericExportBulkAction,
} from "@ryde/controls";
import {
  useCoursesBulkActions,
  useModalConfirm,
  usePageContext,
  useSelectedIds,
} from "@ryde/redux";
import {
  ActionsEnum,
  bulkOperationsDrawerAF,
  ConfirmResult,
} from "@ryde/shared";

import {
  BaseDrawer,
  DrawerContentStyled,
  DrawerHeader,
} from "../../../drawers";

export const BulkOperationsCourseDrawer: FC = () => {
  const { entity } = usePageContext();
  const { t } = useTranslation();
  const { selectedRowsIds, clearSelectedIds } = useSelectedIds(entity);
  const { confirmDialogResult } = useModalConfirm();
  const { bulkDeepRecalculateCourses } = useCoursesBulkActions();
  const genericExportAction = useGenericExportBulkAction();

  const setBulkOperationsDrawerOpened = useSetRecoilState(
    bulkOperationsDrawerAF(entity),
  );

  const onCloseDrawer = useEvent(() => setBulkOperationsDrawerOpened(false));

  const handleDeepRecalculateClick = useEvent(async (): Promise<void> => {
    if (!selectedRowsIds.length) {
      return;
    }

    const confirmationResult = await confirmDialogResult({
      title: t("general.CONFIRM"),
      message: t("general.DEEP_RECALCULATE_MESSAGE"),
    });

    if (confirmationResult !== ConfirmResult.OK) {
      return;
    }

    const result = await bulkDeepRecalculateCourses(selectedRowsIds);

    if (result?.success) {
      clearSelectedIds();
      onCloseDrawer();
    }
  });

  const actions: IBulkActionItem[] = [
    {
      key: "deepRecalculate",
      label: t("general.DEEP_RECALCULATE"),
      hint: t("course.bulk.hints.deepRecalculate"),
      icon: SVG_MUI.ArrowsRotate3SVG,
      permission: { entity: "course", action: ActionsEnum.Update },
      onClick: () => void handleDeepRecalculateClick(),
    },
    genericExportAction,
  ];

  return (
    <BaseDrawer onCloseDrawer={onCloseDrawer}>
      <DrawerHeader
        title={t("general.BULK_ACTIONS")}
        onCloseDrawer={onCloseDrawer}
      />
      <DrawerContentStyled className="bulk-action-drawer">
        <BulkActionsPanel actions={actions} />
      </DrawerContentStyled>
    </BaseDrawer>
  );
};
